import { z } from 'zod';

import {
  AccessTierSchema,
  PermissionScopeAccessSchema,
  UserTypeSchema as PrincipalTypeSchema,
} from '@/features/auth/auth';

const UserListItemSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  // phoneNumber and branchId are not carried on the platform User row
  // post-F9; the backend always serialises them as null. Kept on the type
  // so existing Users-table cells don't have to branch.
  phoneNumber: z.string().nullable().optional(),
  isActive: z.boolean(),
  tenantId: z.string().nullable(),
  branchId: z.string().nullable().optional(),
  // Role is synthesised from userType on the backend ({id, name}); the
  // separate Role table is being deprecated.
  role: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  userType: PrincipalTypeSchema.nullable().optional(),
  accessTier: AccessTierSchema.default('self'),
  // null once the permission matrix has been hand-edited — the list renders
  // that as "custom" so an off-template user is visible at a glance.
  roleTemplateId: z.string().nullable().default(null),
  createdAt: z.string(),
  lastModifiedAt: z.string().nullable().optional(),
});
export type UserListItem = z.infer<typeof UserListItemSchema>;

// The tenant-surface variant of the principal kinds: "admin" maps to
// TenantAdmin (implicit full tenant access via UserContext.is_tenant_super)
// and "user" needs an explicit permissions matrix. "superuser" is excluded —
// only existing superusers can mint new superusers, and that happens through
// the /operators surface, not /users.
export const UserTypeSchema = PrincipalTypeSchema.exclude(['superuser']);
export type UserType = z.infer<typeof UserTypeSchema>;

// `roleTemplateId` is the only access field the portal sends. Naming a
// template makes the server write the userType, the access tier and the whole
// permission matrix from its own catalogue, so the three can never disagree
// with the label — and the client cannot widen a user by composing them
// itself. `userType` stays writable for the admin/user promotion path.
export const CreateUserSchema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  email: z.string().email(),
  phoneNumber: z.string().optional(),
  password: z.string().min(8),
  // Optional because naming a template supplies it: the server writes the
  // userType from its own catalogue. Sending both is how the two drift.
  userType: UserTypeSchema.optional(),
  roleTemplateId: z.string().optional(),
  tenantId: z.string().nullable().optional(),
  branchId: z.string().nullable().optional(),
});
export type CreateUser = z.infer<typeof CreateUserSchema>;

// Must stay a subset of the backend's TenantUserPatch, which is now
// extra="forbid": anything here the API does not declare is a 422, not a
// silently-dropped field behind an HTTP 200. tenantId and branchId are
// deliberately absent — moving a user between tenants is an operators-surface
// action, and branch assignment goes to PUT /v1/users/{id}/branches.
const UpdateUserSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email().optional(),
  phoneNumber: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  userType: UserTypeSchema.optional(),
  roleTemplateId: z.string().optional(),
  // Sent together with roleTemplateId by the permissions tab: the template
  // names what the user was made, the matrix is what they ended up with after
  // any hand-tuning. Sent alone, it clears the template label server-side.
  permissions: z.array(PermissionScopeAccessSchema).optional(),
});
export type UpdateUser = z.infer<typeof UpdateUserSchema>;
